/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { Typography, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { ReactNode } from 'react';

import type { BuilderToggleButtonsProps } from './types';

export function BuilderToggleButtons<T extends string>( {
	selectedOption,
	options,
	onOptionChange
}: BuilderToggleButtonsProps<T> ): ReactNode {
	return (
		<Box
			sx={ {
				overflow: 'clip',
				display: 'flex',
				gap: '4px',
				padding: '2px',
				borderRadius: '4px',
				backgroundColor: '#E3E8ED',
				height: 'fit-content'
			} }
		>
			{ options.map( option => (
				<BuilderToggleButton
					key={ option.type }
					option={ option }
					selectedOption={ selectedOption }
					onOptionChange={ onOptionChange }
				/>
			) ) }
		</Box>
	);
}

function BuilderToggleButton<T extends string>( {
	option,
	selectedOption,
	onOptionChange
}: {
	option: BuilderToggleButtonsProps<T>[ 'options' ][ number ];
	selectedOption: BuilderToggleButtonsProps<T>[ 'selectedOption' ];
	onOptionChange: BuilderToggleButtonsProps<T>[ 'onOptionChange' ];
} ): ReactNode {
	const { palette } = useTheme();
	const isSelected = selectedOption === option.type;

	return (
		<Box
			role={ 'button' }
			className={ isSelected ? 'Mui-selected' : '' }
			onClick={ () => {
				if ( isSelected ) {
					return;
				}

				onOptionChange( option.type );
			} }
			sx={ {
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				width: '120px',
				height: '22px',
				cursor: 'pointer',
				borderRadius: '4px',
				backgroundColor: isSelected ? 'common.white' : 'transparent',
				boxShadow: isSelected ? '0px 2px 8px 0px rgba(0, 0, 0, 0.15)' : 'none'
			} }
		>
			<Typography
				fontSize='14px'
				fontWeight='400'
				lineHeight='22px'
				sx={ {
					color: isSelected ? palette.text.primary : palette.text.secondary,
					width: 'fit-content'
				} }
			>
				{ option.name }
			</Typography>
		</Box>
	);
}
